import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon, PencilIcon, UserIcon, MessageSquareIcon, TagIcon, MapPinIcon, CalendarIcon, ClockIcon, CheckIcon, XIcon, SwitchCameraIcon } from 'lucide-react';
import ProfileSetup from './ProfileSetup';
interface ProfileProps {
  userType: 'mentor' | 'mentee' | null;
  onUserTypeChange: (type: 'mentor' | 'mentee') => void;
}
const Profile: React.FC<ProfileProps> = ({
  userType,
  onUserTypeChange
}) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>({});
  const [expandedSections, setExpandedSections] = useState<string[]>(['basic', 'communication', 'categories']);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [availableUserTypes, setAvailableUserTypes] = useState<('mentor' | 'mentee')[]>([]);
  const [showProfileSwitcher, setShowProfileSwitcher] = useState(false);
  useEffect(() => {
    // Load profile data from localStorage
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedData = JSON.parse(userData);
      if (parsedData.profile) {
        setProfile(parsedData.profile);
      }
      // Determine available user types
      const types: ('mentor' | 'mentee')[] = [];
      if (parsedData.userType) {
        types.push(parsedData.userType);
      }
      // Check if the other profile exists
      const otherType = parsedData.userType === 'mentor' ? 'mentee' : 'mentor';
      const otherUserData = localStorage.getItem(`${otherType}Data`);
      if (otherUserData) {
        types.push(otherType);
      }
      setAvailableUserTypes(types);
    }
  }, [userType]);
  const toggleSection = (section: string) => {
    setExpandedSections(prev => prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]);
  };
  const handleEdit = (section: string) => {
    setEditingSection(section);
  };
  const handleSaveChanges = (updatedProfile: any) => {
    setProfile(updatedProfile);
    setEditingSection(null);
    // Save to localStorage
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedData = JSON.parse(userData);
      localStorage.setItem('userData', JSON.stringify({
        ...parsedData,
        profile: updatedProfile
      }));
    }
  };
  const handleSwitchProfile = (type: 'mentor' | 'mentee') => {
    onUserTypeChange(type);
    setShowProfileSwitcher(false);
  };
  // Map section to onboarding step
  const sectionToStep = {
    basic: 1,
    communication: 2,
    categories: 3
  };
  if (editingSection) {
    return <div className="bg-gray-50 -mx-4 px-4 py-4" data-id="element-1802">
        <div className="flex justify-between items-center mb-4" data-id="element-1803">
          <h2 className="text-xl font-bold" data-id="element-1804">Edit Profile</h2>
          <button onClick={() => setEditingSection(null)} className="text-gray-500 hover:text-gray-700" data-id="element-1805">
            <XIcon size={20} data-id="element-1806" />
          </button>
        </div>
        <ProfileSetup userType={userType} onComplete={() => {}} // Not used in edit mode
      initialStep={sectionToStep[editingSection as keyof typeof sectionToStep]} editMode={true} onSave={handleSaveChanges} initialData={profile} data-id="element-1807" />
      </div>;
  }
  return <div className="bg-gray-50 -mx-4 px-4 py-4" data-id="element-1808">
      <div className="max-w-2xl mx-auto" data-id="element-1809">
        <div className="bg-white rounded-xl shadow-sm mb-4 p-4" data-id="element-1810">
          <div className="flex items-center justify-between mb-4" data-id="element-1811">
            <div className="flex items-center" data-id="element-1812">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mr-4" data-id="element-1813">
                <UserIcon size={32} className="text-purple-600" data-id="element-1814" />
              </div>
              <div data-id="element-1815">
                <h1 className="text-xl font-bold" data-id="element-1816">
                  {profile.name || 'Your Profile'}
                </h1>
                <div className="relative" data-id="element-1817">
                  <button onClick={() => setShowProfileSwitcher(!showProfileSwitcher)} className="text-sm text-purple-600 flex items-center mt-1" data-id="element-1818">
                    Viewing as: {userType === 'mentor' ? 'Mentor' : 'Mentee'}{' '}
                    <ChevronDownIcon size={16} className="ml-1" data-id="element-1819" />
                  </button>
                  {showProfileSwitcher && <div className="absolute top-full left-0 mt-1 bg-white shadow-md rounded-md border border-gray-200 z-10 w-48" data-id="element-1820">
                      {['mentor', 'mentee'].map(type => <button key={type} onClick={() => handleSwitchProfile(type as 'mentor' | 'mentee')} disabled={!availableUserTypes.includes(type as 'mentor' | 'mentee')} className={`w-full text-left px-4 py-2 flex items-center justify-between ${userType === type ? 'bg-purple-50 text-purple-700' : !availableUserTypes.includes(type as 'mentor' | 'mentee') ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-50'}`} data-id="element-1821">
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                          {userType === type && <CheckIcon size={16} data-id="element-1822" />}
                        </button>)}
                      {availableUserTypes.length === 1 && <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-200" data-id="element-1823">
                          Create a {userType === 'mentor' ? 'mentee' : 'mentor'}{' '}
                          profile to switch
                        </div>}
                    </div>}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Basic Information Section */}
        <div className="bg-white rounded-xl shadow-sm mb-4 overflow-hidden" data-id="element-1824">
          <div className="flex justify-between items-center p-4 cursor-pointer" onClick={() => toggleSection('basic')} data-id="element-1825">
            <div className="flex items-center" data-id="element-1826">
              <UserIcon size={20} className="text-purple-600 mr-3" data-id="element-1827" />
              <h2 className="text-lg font-semibold" data-id="element-1828">Basic Information</h2>
            </div>
            <div className="flex items-center" data-id="element-1829">
              <button onClick={e => {
              e.stopPropagation();
              handleEdit('basic');
            }} className="text-purple-600 mr-3 flex items-center text-sm" data-id="element-1830">
                <PencilIcon size={16} className="mr-1" data-id="element-1831" /> Edit
              </button>
              {expandedSections.includes('basic') ? <ChevronUpIcon size={20} className="text-gray-400" data-id="element-1832" /> : <ChevronDownIcon size={20} className="text-gray-400" data-id="element-1833" />}
            </div>
          </div>
          {expandedSections.includes('basic') && <div className="p-4 pt-0 border-t border-gray-100" data-id="element-1834">
              <div className="space-y-3" data-id="element-1835">
                <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1836">
                  <span className="text-gray-700" data-id="element-1837">Full Name</span>
                  <span className="font-semibold text-gray-900" data-id="element-1838">
                    {profile.name || 'Not specified'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1839">
                  <span className="text-gray-700" data-id="element-1840">Pronouns</span>
                  <span className="font-semibold text-gray-900" data-id="element-1841">
                    {profile.pronouns && profile.pronouns.length > 0 ? profile.pronouns.join(', ') : 'Not specified'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1842">
                  <span className="text-gray-700" data-id="element-1843">LGBTQ+ Affiliation</span>
                  <span className="font-semibold text-gray-900" data-id="element-1844">
                    {profile.lgbtqAffiliation && profile.lgbtqAffiliation.length > 0 ? profile.lgbtqAffiliation.join(', ') : 'Not specified'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1845">
                  <span className="text-gray-700" data-id="element-1846">Gender Identity</span>
                  <span className="font-semibold text-gray-900" data-id="element-1847">
                    {profile.genderIdentity && profile.genderIdentity.length > 0 ? profile.genderIdentity.join(', ') : 'Not specified'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1848">
                  <span className="text-gray-700" data-id="element-1849">Race & Ethnicity</span>
                  <span className="font-semibold text-gray-900" data-id="element-1850">
                    {profile.raceEthnicity && profile.raceEthnicity.length > 0 ? profile.raceEthnicity.join(', ') : 'Not specified'}
                  </span>
                </div>
                <div className="flex justify-between py-2" data-id="element-1851">
                  <span className="text-gray-700" data-id="element-1852">Disability Status</span>
                  <span className="font-semibold text-gray-900" data-id="element-1853">
                    {profile.accessibility && profile.accessibility.length > 0 ? profile.accessibility.join(', ') : 'Not specified'}
                  </span>
                </div>
              </div>
            </div>}
        </div>
        {/* Communication Preferences Section */}
        <div className="bg-white rounded-xl shadow-sm mb-4 overflow-hidden" data-id="element-1854">
          <div className="flex justify-between items-center p-4 cursor-pointer" onClick={() => toggleSection('communication')} data-id="element-1855">
            <div className="flex items-center" data-id="element-1856">
              <MessageSquareIcon size={20} className="text-purple-600 mr-3" data-id="element-1857" />
              <h2 className="text-lg font-semibold" data-id="element-1858">
                Communication Preferences
              </h2>
            </div>
            <div className="flex items-center" data-id="element-1859">
              <button onClick={e => {
              e.stopPropagation();
              handleEdit('communication');
            }} className="text-purple-600 mr-3 flex items-center text-sm" data-id="element-1860">
                <PencilIcon size={16} className="mr-1" data-id="element-1861" /> Edit
              </button>
              {expandedSections.includes('communication') ? <ChevronUpIcon size={20} className="text-gray-400" data-id="element-1862" /> : <ChevronDownIcon size={20} className="text-gray-400" data-id="element-1863" />}
            </div>
          </div>
          {expandedSections.includes('communication') && <div className="p-4 pt-0 border-t border-gray-100" data-id="element-1864">
              <div className="space-y-3" data-id="element-1865">
                <div className="py-2 border-b border-gray-100" data-id="element-1866">
                  <span className="text-gray-700 block mb-1" data-id="element-1867">
                    Why do you want to be a {userType}?
                  </span>
                  <p className="text-gray-900 line-clamp-2" data-id="element-1868">
                    {profile.mentorshipReason || 'Not specified'}
                  </p>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1869">
                  <span className="text-gray-700" data-id="element-1870">Communication Style</span>
                  <span className="font-semibold text-gray-900" data-id="element-1871">
                    {profile.communicationStyle && profile.communicationStyle.length > 0 ? profile.communicationStyle.map(style => style.split(' - ')[0]).join(', ') : 'Not specified'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100" data-id="element-1872">
                  <div className="flex items-center" data-id="element-1873">
                    <ClockIcon size={16} className="text-gray-500 mr-2" data-id="element-1874" />
                    <span className="text-gray-700" data-id="element-1875">Preferred Cadence</span>
                  </div>
                  <span className="font-semibold text-gray-900" data-id="element-1876">
                    {profile.cadence && profile.cadence.length > 0 ? profile.cadence.join(', ') : 'Not specified'}
                  </span>
                </div>
                <div className="flex justify-between py-2" data-id="element-1877">
                  <div className="flex items-center" data-id="element-1878">
                    <MapPinIcon size={16} className="text-gray-500 mr-2" data-id="element-1879" />
                    <span className="text-gray-700" data-id="element-1880">Location</span>
                  </div>
                  <span className="font-semibold text-gray-900" data-id="element-1881">
                    {profile.location || 'Not specified'}
                  </span>
                </div>
              </div>
            </div>}
        </div>
        {/* Mentorship Categories Section */}
        <div className="bg-white rounded-xl shadow-sm mb-4 overflow-hidden" data-id="element-1882">
          <div className="flex justify-between items-center p-4 cursor-pointer" onClick={() => toggleSection('categories')} data-id="element-1883">
            <div className="flex items-center" data-id="element-1884">
              <TagIcon size={20} className="text-purple-600 mr-3" data-id="element-1885" />
              <h2 className="text-lg font-semibold" data-id="element-1886">Mentorship Categories</h2>
            </div>
            <div className="flex items-center" data-id="element-1887">
              <button onClick={e => {
              e.stopPropagation();
              handleEdit('categories');
            }} className="text-purple-600 mr-3 flex items-center text-sm" data-id="element-1888">
                <PencilIcon size={16} className="mr-1" data-id="element-1889" /> Edit
              </button>
              {expandedSections.includes('categories') ? <ChevronUpIcon size={20} className="text-gray-400" data-id="element-1890" /> : <ChevronDownIcon size={20} className="text-gray-400" data-id="element-1891" />}
            </div>
          </div>
          {expandedSections.includes('categories') && <div className="p-4 pt-0 border-t border-gray-100" data-id="element-1892">
              <p className="text-sm text-gray-600 mb-3" data-id="element-1893">
                {userType === 'mentor' ? 'Categories you provide mentorship in:' : 'Categories you seek mentorship in:'}
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-1894">
                {profile.mentorshipCategories && profile.mentorshipCategories.map((category: string) => <span key={category} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-purple-50 text-purple-700" data-id="element-1895">
                      {getCategoryEmoji(category)} {category}
                    </span>)}
                {(!profile.mentorshipCategories || profile.mentorshipCategories.length === 0) && <span className="text-gray-500" data-id="element-1896">No categories selected</span>}
              </div>
            </div>}
        </div>
      </div>
    </div>;
};
// Helper function to get emoji for category
function getCategoryEmoji(category: string): string {
  const emojiMap: {
    [key: string]: string;
  } = {
    Career: '💼',
    Academic: '🎓',
    Creative: '🎨',
    Wellness: '💪',
    Growth: '🌱',
    Finance: '🧾',
    Identity: '🧭',
    Community: '🫶',
    Focus: '🧠',
    Goals: '🎯',
    Life: '🧳',
    Parenting: '🍼'
  };
  return emojiMap[category] || '📋';
}
export default Profile;